import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Plus, UploadCloud, Loader2, Trash2, FileSpreadsheet, ChevronDown, ChevronUp, HandCoins } from 'lucide-react';

const enconOrange = '#ee7203';

/**
 * Settings > Pricing > "Manufacturer Price Support" tab.
 * Reuses the same supplier list as the main Pricing tab (Knauf etc.) —
 * documents are just an extra thing each supplier can have. Uploading a
 * multi-tab workbook (see routes/mf_support.py for the expected columns)
 * turns each tab into one Document; re-uploading a tab with the same name
 * fully replaces its entries.
 */
const PricingMfSupportTab = ({ suppliers, onSuppliersChanged }) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!selectedSupplierId && suppliers.length > 0) {
      setSelectedSupplierId(suppliers[0].id);
    }
  }, [suppliers, selectedSupplierId]);

  const loadDocuments = async (supplierId) => {
    if (!supplierId) { setDocuments([]); return; }
    setLoadingDocs(true);
    try {
      const res = await api.get(`${API}/pricing/mf-support/documents`, { params: { supplier_id: supplierId } });
      setDocuments(res.data?.documents || []);
    } catch {
      toast.error('Failed to load support documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => { loadDocuments(selectedSupplierId); }, [selectedSupplierId]);

  const submitNewSupplier = async () => {
    if (!newSupplierName.trim()) return;
    try {
      const res = await api.post(`${API}/pricing/suppliers`, { name: newSupplierName });
      toast.success('Supplier added');
      setNewSupplierName(''); setAddingSupplier(false);
      onSuppliersChanged();
      if (res.data?.id) setSelectedSupplierId(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add supplier');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSupplierId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/pricing/mf-support/suppliers/${selectedSupplierId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      loadDocuments(selectedSupplierId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}" and its ${doc.entry_count} support entries? This cannot be undone.`)) return;
    try {
      await api.delete(`${API}/pricing/mf-support/documents/${doc.id}`);
      toast.success('Document deleted');
      if (expandedDoc === doc.id) setExpandedDoc(null);
      loadDocuments(selectedSupplierId);
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const toggleExpand = async (doc) => {
    if (expandedDoc === doc.id) { setExpandedDoc(null); return; }
    setExpandedDoc(doc.id);
    setLoadingEntries(true);
    try {
      const res = await api.get(`${API}/pricing/mf-support/documents/${doc.id}/entries`);
      setEntries(res.data?.entries || []);
    } catch {
      toast.error('Failed to load entries');
    } finally {
      setLoadingEntries(false);
    }
  };

  return (
    <div data-testid="pricing-mf-support-tab">
      <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4">
        <HandCoins className="w-4 h-4" style={{ color: enconOrange }} />
        Manufacturer support agreements (e.g. Knauf Merchant Support) — each document is a tab from the uploaded
        workbook, listing a pence-per-unit support value per product code. Used on Create Quote's MF Support box.
      </p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select
          value={selectedSupplierId}
          onChange={(e) => setSelectedSupplierId(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white min-w-[200px]"
          data-testid="mf-support-supplier-select"
        >
          {suppliers.length === 0 && <option value="">No suppliers yet</option>}
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        {addingSupplier ? (
          <>
            <Input
              placeholder="New supplier / manufacturer name"
              value={newSupplierName}
              onChange={(e) => setNewSupplierName(e.target.value)}
              className="w-56"
              data-testid="mf-support-new-supplier-input"
            />
            <Button onClick={submitNewSupplier} style={{ backgroundColor: enconOrange }} className="text-white" data-testid="mf-support-new-supplier-submit">Add</Button>
            <Button variant="outline" onClick={() => setAddingSupplier(false)}>Cancel</Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => setAddingSupplier(true)} data-testid="mf-support-add-supplier-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Supplier
          </Button>
        )}

        <div className="flex-1" />

        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="mf-support-upload-file-input" />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={!selectedSupplierId || uploading}
          style={{ backgroundColor: enconOrange }}
          className="text-white"
          data-testid="mf-support-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1" />}
          Upload Support Document(s)
        </Button>
      </div>

      {loadingDocs ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : !selectedSupplierId ? (
        <div className="text-center py-16 text-gray-400"><p>Add a supplier to start uploading support documents.</p></div>
      ) : documents.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>No support documents yet for this supplier — upload a workbook to get started. Each tab in the file becomes a document here.</p>
        </div>
      ) : (
        <div className="grid gap-2">
          {documents.map((doc) => (
            <div key={doc.id} className="border rounded-xl overflow-hidden" data-testid={`mf-support-doc-${doc.id}`}>
              <div className="flex items-center justify-between p-3">
                <button onClick={() => toggleExpand(doc)} className="flex items-center gap-2 min-w-0 text-left flex-1" data-testid={`mf-support-doc-expand-${doc.id}`}>
                  <FileSpreadsheet className="w-4 h-4 shrink-0" style={{ color: enconOrange }} />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{doc.name}</p>
                    <p className="text-xs text-gray-400">
                      {doc.entry_count} products{doc.document_number ? ` · Ref ${doc.document_number}` : ''}
                    </p>
                  </div>
                  {expandedDoc === doc.id ? <ChevronUp className="w-4 h-4 text-gray-300 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-300 shrink-0" />}
                </button>
                <button
                  onClick={() => deleteDocument(doc)}
                  className="p-1.5 text-red-400 hover:bg-red-50 rounded ml-2"
                  data-testid={`mf-support-doc-delete-${doc.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {expandedDoc === doc.id && (
                <div className="border-t bg-gray-50 max-h-64 overflow-y-auto">
                  {loadingEntries ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin" style={{ color: enconOrange }} /></div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-gray-400 sticky top-0 bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-1.5">M4 Code</th>
                          <th className="text-left px-3 py-1.5">Description</th>
                          <th className="text-right px-3 py-1.5">Support</th>
                          <th className="text-left px-3 py-1.5">UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((e) => (
                          <tr key={e.id} className="border-t border-gray-100">
                            <td className="px-3 py-1 font-mono">{e.stock_code}</td>
                            <td className="px-3 py-1 text-gray-500 truncate max-w-xs">{e.description}</td>
                            <td className="px-3 py-1 text-right font-semibold" style={{ color: enconOrange }}>{(e.support_value * 100).toFixed(1)}p</td>
                            <td className="px-3 py-1 text-gray-400">{e.uom}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PricingMfSupportTab;
